import React from "react";
import Card from "components/card";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  MdNavigateBefore,
  MdNavigateNext,
  MdClose,
  MdDownload,
  MdVerified,
} from "react-icons/md";
import { classMgmtService } from "services/admin/classMgmtService";
import { faceCheckLogService } from "services/admin/faceCheckLogService";
import {
  faceService,
  type FaceEmbeddingItem,
} from "services/admin/faceService";
import { userService } from "services/admin/userService";
import type {
  FaceCheckLogItem,
  FaceCheckLogVerifyResult,
  SessionParticipant,
  UserListItem,
} from "types/domain";
import ActionButton from "components/ui/ActionButton";
import StatusBadge from "components/ui/StatusBadge";
import { useLanguage } from "context/LanguageContext";

const columnHelper = createColumnHelper<FaceCheckLogItem>();

function formatDateTime(iso: string, locale: string) {
  const d = new Date(iso);
  return d.toLocaleString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function ScoreBadge({ score }: { score: number | null }) {
  if (score == null) return <span className="text-ink-subtle">—</span>;
  const pct = Math.round(score * 100);
  const tone = pct >= 80 ? "success" : pct >= 60 ? "warning" : "danger";
  return <StatusBadge label={`${pct}%`} tone={tone} />;
}

function MatchBadge({ matched }: { matched: boolean }) {
  const { t } = useLanguage();
  return matched ? (
    <StatusBadge label={t("faceLogs.matched")} tone="success" />
  ) : (
    <StatusBadge label={t("faceLogs.notMatched")} tone="danger" />
  );
}

async function downloadImage(url: string, filename: string) {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  } catch {
    // Fallback: open in new tab
    window.open(url, "_blank");
  }
}

export default function FaceCheckLogs() {
  const { t, locale } = useLanguage();
  const [data, setData] = React.useState<FaceCheckLogItem[]>([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [pagination, setPagination] = React.useState({ page: 1, limit: 25 });
  const [matchedFilter, setMatchedFilter] = React.useState<string>("ALL");
  const [dateFrom, setDateFrom] = React.useState("");
  const [dateTo, setDateTo] = React.useState("");
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [verifyModal, setVerifyModal] = React.useState<{
    logId: string;
    sessionId: string | null;
    loading: boolean;
    result: FaceCheckLogVerifyResult | null;
    error: string | null;
  } | null>(null);
  const [sessionParticipants, setSessionParticipants] = React.useState<
    SessionParticipant[]
  >([]);
  const [participantsLoading, setParticipantsLoading] = React.useState(false);
  const [compareSelectedUserId, setCompareSelectedUserId] =
    React.useState<string>("");

  const handleVerify = async (
    logId: string,
    sessionId: string | null,
    compareUserId?: string
  ) => {
    setVerifyModal({
      logId,
      sessionId,
      loading: true,
      result: null,
      error: null,
    });
    try {
      const result = await faceCheckLogService.verify(logId, compareUserId);
      setVerifyModal({ logId, sessionId, loading: false, result, error: null });
    } catch (err) {
      setVerifyModal({
        logId,
        sessionId,
        loading: false,
        result: null,
        error: err instanceof Error ? err.message : "faceLogs.verifyFailed",
      });
    }
  };

  const openVerifyModal = async (logId: string, sessionId: string | null) => {
    setCompareSelectedUserId("");
    setSessionParticipants([]);
    handleVerify(logId, sessionId);

    if (sessionId) {
      setParticipantsLoading(true);
      try {
        const participants = await classMgmtService.getSessionParticipants(
          sessionId
        );
        setSessionParticipants(participants);
      } catch {
        setSessionParticipants([]);
      } finally {
        setParticipantsLoading(false);
      }
    }
  };

  const [faceModal, setFaceModal] = React.useState<{
    userId: string;
    userName: string;
    embeddings: FaceEmbeddingItem[];
    loading: boolean;
  } | null>(null);

  const openFaceModal = async (userId: string, userName: string) => {
    setFaceModal({ userId, userName, embeddings: [], loading: true });
    try {
      const items = await faceService.list(userId);
      setFaceModal((prev) =>
        prev ? { ...prev, embeddings: items, loading: false } : null
      );
    } catch {
      setFaceModal((prev) => (prev ? { ...prev, loading: false } : null));
    }
  };
  const [userSearch, setUserSearch] = React.useState("");
  const [userResults, setUserResults] = React.useState<UserListItem[]>([]);
  const [selectedUser, setSelectedUser] = React.useState<UserListItem | null>(
    null
  );
  const [showUserDropdown, setShowUserDropdown] = React.useState(false);
  const userSearchRef = React.useRef<HTMLDivElement>(null);
  const searchTimerRef = React.useRef<ReturnType<typeof setTimeout>>();

  // Close dropdown on outside click
  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        userSearchRef.current &&
        !userSearchRef.current.contains(e.target as Node)
      ) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Debounced user search
  const handleUserSearchChange = (value: string) => {
    setUserSearch(value);
    if (!value.trim()) {
      setUserResults([]);
      setShowUserDropdown(false);
      return;
    }
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const res = await userService.getAll({ limit: 10, skip: 0 });
        const keyword = value.trim().toLowerCase();
        const filtered = res.items.filter(
          (u) =>
            u.full_name.toLowerCase().includes(keyword) ||
            (u.email && u.email.toLowerCase().includes(keyword))
        );
        setUserResults(filtered);
        setShowUserDropdown(true);
      } catch {
        setUserResults([]);
      }
    }, 300);
  };

  const selectUser = (user: UserListItem) => {
    setSelectedUser(user);
    setUserSearch(user.full_name);
    setShowUserDropdown(false);
    setPagination((p) => ({ ...p, page: 1 }));
  };

  const clearUser = () => {
    setSelectedUser(null);
    setUserSearch("");
    setUserResults([]);
    setPagination((p) => ({ ...p, page: 1 }));
  };

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    try {
      const skip = (pagination.page - 1) * pagination.limit;
      const params: Record<string, unknown> = {
        skip,
        limit: pagination.limit,
      };
      if (matchedFilter === "true") params.matched = true;
      if (matchedFilter === "false") params.matched = false;
      if (selectedUser) params.user_id = selectedUser.id;
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) {
        const end = new Date(dateTo);
        end.setHours(23, 59, 59, 999);
        params.date_to = end.toISOString();
      }
      const res = await faceCheckLogService.list(
        params as Parameters<typeof faceCheckLogService.list>[0]
      );
      setData(res.items);
      setTotal(res.total);
    } catch (err) {
      console.error("Failed to fetch face check logs:", err);
    } finally {
      setLoading(false);
    }
  }, [pagination, matchedFilter, selectedUser, dateFrom, dateTo]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalPages = Math.ceil(total / pagination.limit) || 1;

  const columns = React.useMemo(
    () => [
      columnHelper.display({
        id: "number",
        header: () => <p className="text-sm font-bold">#</p>,
        cell: (info) => (
          <p className="text-sm">
            {(pagination.page - 1) * pagination.limit + info.row.index + 1}
          </p>
        ),
        size: 50,
      }),
      columnHelper.accessor("snapshot_url", {
        header: () => (
          <p className="text-sm font-bold">{t("faceLogs.col.photo")}</p>
        ),
        cell: (info) => {
          const url = info.getValue();
          const row = info.row.original;
          const filename = `face-check-${row.id.slice(0, 8)}.jpg`;
          return url ? (
            <div className="flex items-center gap-1.5">
              <img
                src={url}
                alt="snapshot"
                className="h-12 w-12 cursor-pointer rounded-lg object-cover"
                onClick={() => setPreviewUrl(url)}
              />
              <ActionButton
                variant="ghost"
                size="sm"
                onClick={() => downloadImage(url, filename)}
                className="p-1 text-ink-subtle hover:text-gray-600"
                title={t("faceLogs.downloadImage")}
              >
                <MdDownload className="h-4 w-4" />
              </ActionButton>
            </div>
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100 text-xs text-ink-subtle">
              N/A
            </div>
          );
        },
        size: 100,
      }),
      columnHelper.accessor("user_full_name", {
        header: () => (
          <p className="text-sm font-bold">{t("faceLogs.col.user")}</p>
        ),
        cell: (info) => {
          const name = info.getValue();
          const email = info.row.original.user_email;
          const userId = info.row.original.user_id;
          return name && userId ? (
            <button
              onClick={() => openFaceModal(userId, name)}
              className="text-left hover:underline"
            >
              <p className="text-sm font-medium text-brand-500">{name}</p>
              {email && <p className="text-xs text-ink-muted">{email}</p>}
            </button>
          ) : (
            <p className="text-sm italic text-ink-subtle">
              {t("faceLogs.unrecognized")}
            </p>
          );
        },
      }),
      columnHelper.accessor("score", {
        header: () => (
          <p className="text-sm font-bold">{t("faceLogs.col.score")}</p>
        ),
        cell: (info) => <ScoreBadge score={info.getValue()} />,
        size: 90,
      }),
      columnHelper.accessor("matched", {
        header: () => (
          <p className="text-sm font-bold">{t("faceLogs.col.result")}</p>
        ),
        cell: (info) => <MatchBadge matched={info.getValue()} />,
        size: 110,
      }),
      columnHelper.accessor("session_title", {
        header: () => (
          <p className="text-sm font-bold">{t("faceLogs.col.session")}</p>
        ),
        cell: (info) => <p className="text-sm">{info.getValue() || "—"}</p>,
      }),
      columnHelper.accessor("created_at", {
        header: () => (
          <p className="text-sm font-bold">{t("faceLogs.col.time")}</p>
        ),
        cell: (info) => (
          <p className="text-sm text-ink">
            {formatDateTime(info.getValue(), locale)}
          </p>
        ),
        size: 160,
      }),
      columnHelper.display({
        id: "actions",
        header: () => (
          <p className="text-sm font-bold">{t("faceLogs.col.verify")}</p>
        ),
        cell: (info) => {
          const row = info.row.original;
          return row.snapshot_url ? (
            <ActionButton
              variant="ghost"
              size="sm"
              onClick={() => openVerifyModal(row.id, row.session_id)}
              className="bg-brand-50 px-2.5 py-1.5 text-xs font-medium text-brand-600 hover:bg-brand-100 dark:bg-brand-500/15 dark:text-brand-300 dark:hover:bg-brand-500/25"
              title={t("faceLogs.reverify")}
              startIcon={<MdVerified className="h-4 w-4" />}
            >
              {t("faceLogs.verify")}
            </ActionButton>
          ) : (
            <span className="text-xs text-ink-subtle">—</span>
          );
        },
        size: 100,
      }),
    ],
    [pagination.page, pagination.limit, t, locale]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="mt-5 px-4">
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
      <Card extra="w-full px-6 pb-6">
        <div className="pt-4">
          <h2 className="text-xl font-bold text-ink">
            {t("faceLogs.title")}
          </h2>

          {/* Filters */}
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
            <div className="relative" ref={userSearchRef}>
              <label className="mb-1 block text-xs font-medium text-ink-muted">
                {t("faceLogs.col.user")}
              </label>
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  value={userSearch}
                  onChange={(e) => handleUserSearchChange(e.target.value)}
                  onFocus={() => {
                    if (userResults.length > 0) setShowUserDropdown(true);
                  }}
                  placeholder={t("faceLogs.searchUserPlaceholder")}
                  className="w-56 rounded-lg border border-line bg-surface px-3 py-2 text-sm"
                />
                {selectedUser && (
                  <ActionButton
                    variant="ghost"
                    size="sm"
                    onClick={clearUser}
                    className="p-1 text-ink-subtle hover:text-gray-600"
                    title={t("action.clearFilters")}
                  >
                    ✕
                  </ActionButton>
                )}
              </div>
              {showUserDropdown && userResults.length > 0 && (
                <div className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border border-line bg-surface shadow-lg">
                  {userResults.map((u) => (
                    <button
                      key={u.id}
                      onClick={() => selectUser(u)}
                      className="flex w-full flex-col px-3 py-2 text-left hover:bg-surface-hover"
                    >
                      <span className="text-sm font-medium">{u.full_name}</span>
                      {u.email && (
                        <span className="text-xs text-ink-muted">{u.email}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-muted">
                {t("faceLogs.fromDate")}
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPagination((p) => ({ ...p, page: 1 }));
                }}
                className="rounded-lg border border-line bg-surface px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-muted">
                {t("faceLogs.toDate")}
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPagination((p) => ({ ...p, page: 1 }));
                }}
                className="rounded-lg border border-line bg-surface px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-muted">
                {t("faceLogs.col.result")}
              </label>
              <select
                value={matchedFilter}
                onChange={(e) => {
                  setMatchedFilter(e.target.value);
                  setPagination((p) => ({ ...p, page: 1 }));
                }}
                className="rounded-lg border border-line bg-surface px-3 py-2 text-sm"
              >
                <option value="ALL">{t("faceLogs.filterAll")}</option>
                <option value="true">{t("faceLogs.matched")}</option>
                <option value="false">{t("faceLogs.notMatched")}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="mt-6 overflow-x-auto">
          {loading ? (
            <div className="space-y-3 py-4">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="h-12 w-full animate-pulse rounded-lg bg-gray-200"
                />
              ))}
            </div>
          ) : data.length === 0 ? (
            <p className="py-10 text-center text-sm text-ink-muted">
              {t("faceLogs.empty")}
            </p>
          ) : (
            <table className="w-full">
              <thead>
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id}>
                    {hg.headers.map((header) => (
                      <th
                        key={header.id}
                        className="border-b border-line py-3 pr-4 text-start"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b border-line align-middle"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="py-3 pr-4 align-middle">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {!loading && data.length > 0 && (
          <div className="mt-6 flex items-center justify-between border-t border-line pt-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">
                {t("faceLogs.rowsPerPage")}
              </label>
              <select
                value={pagination.limit}
                onChange={(e) =>
                  setPagination({
                    page: 1,
                    limit: parseInt(e.target.value, 10),
                  })
                }
                className="rounded-lg border border-line bg-surface px-3 py-2 text-sm"
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-ink-muted">
                {t("faceLogs.totalRecords", {
                  count: total.toLocaleString(locale),
                })}
              </span>
              <ActionButton
                variant="ghost"
                size="sm"
                onClick={() =>
                  setPagination((p) => ({
                    ...p,
                    page: Math.max(1, p.page - 1),
                  }))
                }
                disabled={pagination.page === 1}
                className="border border-line font-medium text-ink hover:bg-surface-hover"
                startIcon={<MdNavigateBefore className="h-4 w-4" />}
              >
                {t("faceLogs.prev")}
              </ActionButton>
              <span className="min-w-[120px] text-center text-sm font-medium text-gray-700">
                {t("faceLogs.pageOf", {
                  page: pagination.page,
                  total: totalPages,
                })}
              </span>
              <ActionButton
                variant="ghost"
                size="sm"
                onClick={() =>
                  setPagination((p) => ({ ...p, page: p.page + 1 }))
                }
                disabled={pagination.page >= totalPages}
                className="border border-line font-medium text-ink hover:bg-surface-hover"
                endIcon={<MdNavigateNext className="h-4 w-4" />}
              >
                {t("faceLogs.next")}
              </ActionButton>
            </div>
          </div>
        )}
      </Card>

      {/* Verify result modal */}
      {verifyModal && (
        <div
          className="bg-black/60 fixed inset-0 z-50 flex items-center justify-center px-4"
          onClick={() => setVerifyModal(null)}
        >
          <div
            className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-surface p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-ink">
                {t("faceLogs.verifyResultTitle")}
              </h3>
              <ActionButton
                variant="ghost"
                size="sm"
                onClick={() => setVerifyModal(null)}
                className="p-1.5 text-ink-subtle"
              >
                <MdClose className="h-5 w-5" />
              </ActionButton>
            </div>

            {/* User selector for comparison — session participants */}
            {verifyModal.sessionId && (
              <div className="mb-4 rounded-lg border border-line bg-surface-muted p-3">
                <label className="mb-1.5 block text-xs font-medium text-ink-muted">
                  {t("faceLogs.compareWithParticipant")}
                </label>
                <div className="flex items-center gap-2">
                  {participantsLoading ? (
                    <div className="flex flex-1 items-center gap-2 py-1">
                      <div className="border-t-transparent h-5 w-5 animate-spin rounded-full border-2 border-brand-500" />
                      <span className="text-sm text-ink-muted">
                        {t("state.loading")}
                      </span>
                    </div>
                  ) : (
                    <select
                      value={compareSelectedUserId}
                      onChange={(e) => setCompareSelectedUserId(e.target.value)}
                      className="flex-1 rounded-lg border border-line bg-surface px-3 py-2 text-sm"
                    >
                      <option value="">{t("faceLogs.originalUser")}</option>
                      {sessionParticipants.map((p) => (
                        <option key={p.id} value={p.user_id}>
                          {p.user?.full_name || p.user_id}
                          {p.user?.email ? ` (${p.user.email})` : ""}
                          {` — ${t(`enum.role.${p.role}`)}`}
                        </option>
                      ))}
                    </select>
                  )}
                  <ActionButton
                    variant="primary"
                    size="sm"
                    onClick={() =>
                      handleVerify(
                        verifyModal.logId,
                        verifyModal.sessionId,
                        compareSelectedUserId || undefined
                      )
                    }
                    disabled={verifyModal.loading}
                    className="whitespace-nowrap"
                  >
                    {t("faceLogs.compare")}
                  </ActionButton>
                </div>
              </div>
            )}

            {verifyModal.loading ? (
              <div className="flex flex-col items-center gap-3 py-12">
                <div className="border-t-transparent h-8 w-8 animate-spin rounded-full border-4 border-brand-500" />
                <p className="text-sm text-ink-muted">
                  {t("faceLogs.verifying")}
                </p>
              </div>
            ) : verifyModal.error ? (
              <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600 dark:bg-red-500/15 dark:text-red-300">
                {t(verifyModal.error)}
              </div>
            ) : verifyModal.result ? (
              <div className="space-y-5">
                {/* Summary */}
                <div className="flex items-start gap-4">
                  {verifyModal.result.snapshot_url && (
                    <img
                      src={verifyModal.result.snapshot_url}
                      alt="Snapshot"
                      className="h-24 w-24 rounded-lg object-cover"
                    />
                  )}
                  <div className="space-y-1">
                    <p className="text-sm">
                      <span className="font-medium text-ink-muted">
                        {t("faceLogs.userLabel")}{" "}
                      </span>
                      {verifyModal.result.user_full_name ||
                        t("faceLogs.unknownUser")}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium text-ink-muted">
                        {t("faceLogs.bestScoreLabel")}{" "}
                      </span>
                      {verifyModal.result.best_score != null ? (
                        <span
                          className={`font-semibold ${
                            verifyModal.result.best_score >= 0.8
                              ? "text-green-600 dark:text-green-300"
                              : verifyModal.result.best_score >= 0.6
                              ? "text-yellow-600 dark:text-yellow-300"
                              : "text-red-600 dark:text-red-300"
                          }`}
                        >
                          {Math.round(verifyModal.result.best_score * 100)}%
                        </span>
                      ) : (
                        "—"
                      )}
                    </p>
                    <p className="text-sm text-ink-muted">
                      {verifyModal.result.message}
                    </p>
                  </div>
                </div>

                {/* Embedding scores */}
                {verifyModal.result.embedding_scores.length > 0 && (
                  <div>
                    <h4 className="mb-2 text-sm font-bold text-ink">
                      {t("faceLogs.compareWithRegistered")}
                    </h4>
                    <div className="space-y-2">
                      {verifyModal.result.embedding_scores.map((es) => (
                        <div
                          key={es.embedding_id}
                          className="flex items-center gap-3 rounded-lg border border-line p-3"
                        >
                          {es.source_image_url ? (
                            <img
                              src={es.source_image_url}
                              alt={es.label}
                              className="h-16 w-16 cursor-pointer rounded-lg object-cover hover:opacity-80"
                              onClick={() => {
                                setVerifyModal(null);
                                setPreviewUrl(es.source_image_url);
                              }}
                            />
                          ) : (
                            <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-gray-100 text-xs text-ink-subtle">
                              N/A
                            </div>
                          )}
                          <div className="flex-1">
                            <p className="text-sm font-medium">{es.label}</p>
                            <div className="mt-1 flex items-center gap-2">
                              <div className="h-2 flex-1 rounded-full bg-gray-200">
                                <div
                                  className={`h-2 rounded-full ${
                                    es.similarity >= 0.8
                                      ? "bg-green-500"
                                      : es.similarity >= 0.6
                                      ? "bg-yellow-500"
                                      : "bg-red-500"
                                  }`}
                                  style={{
                                    width: `${Math.max(
                                      0,
                                      Math.min(100, es.similarity * 100)
                                    )}%`,
                                  }}
                                />
                              </div>
                              <span
                                className={`text-sm font-semibold ${
                                  es.similarity >= 0.8
                                    ? "text-green-600 dark:text-green-300"
                                    : es.similarity >= 0.6
                                    ? "text-yellow-600 dark:text-yellow-300"
                                    : "text-red-600 dark:text-red-300"
                                }`}
                              >
                                {Math.round(es.similarity * 100)}%
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Face embeddings panel (slide from left) */}
      {faceModal && (
        <div
          className="bg-black/60 fixed inset-0 z-50 flex justify-end"
          onClick={() => setFaceModal(null)}
        >
          <div
            style={{ animation: "slideInRight 0.25s ease-out" }}
            className="relative h-full w-full max-w-md overflow-y-auto bg-surface p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-ink">
                {t("faceLogs.facePhotosTitle", { name: faceModal.userName })}
              </h3>
              <ActionButton
                variant="ghost"
                size="sm"
                onClick={() => setFaceModal(null)}
                className="p-1.5 text-ink-subtle"
              >
                <MdClose className="h-5 w-5" />
              </ActionButton>
            </div>

            {faceModal.loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="border-t-transparent h-8 w-8 animate-spin rounded-full border-4 border-brand-500" />
              </div>
            ) : faceModal.embeddings.length === 0 ? (
              <p className="py-8 text-center text-sm text-ink-muted">
                {t("faceLogs.noFacePhotos")}
              </p>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                {faceModal.embeddings.map((emb) => (
                  <div key={emb.id} className="space-y-1">
                    {emb.image_url ? (
                      <div className="group relative">
                        <img
                          src={emb.image_url}
                          alt={emb.label}
                          className="h-28 w-full cursor-pointer rounded-lg object-cover hover:opacity-80"
                          onClick={() => {
                            setFaceModal(null);
                            setPreviewUrl(emb.image_url);
                          }}
                        />
                        <ActionButton
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            downloadImage(
                              emb.image_url!,
                              `face-${emb.label}-${emb.id.slice(0, 8)}.jpg`
                            )
                          }
                          className="bg-black/50 hover:bg-black/70 absolute bottom-1 right-1 rounded p-1 text-white opacity-0 transition-opacity hover:text-white group-hover:opacity-100"
                          title={t("faceLogs.downloadImage")}
                        >
                          <MdDownload className="h-4 w-4" />
                        </ActionButton>
                      </div>
                    ) : (
                      <div className="flex h-28 w-full items-center justify-center rounded-lg bg-gray-100 text-xs text-ink-subtle">
                        {t("faceLogs.noImage")}
                      </div>
                    )}
                    <p className="text-center text-xs font-medium text-ink">
                      {emb.label}
                    </p>
                    <p className="text-center text-[10px] text-ink-subtle">
                      {new Date(emb.created_at).toLocaleDateString(locale)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Image preview modal */}
      {previewUrl && (
        <div
          className="bg-black/60 fixed inset-0 z-50 flex items-center justify-center px-4"
          onClick={() => setPreviewUrl(null)}
        >
          <div
            className="relative max-h-[85vh] max-w-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute -right-3 -top-3 flex gap-1.5">
              <ActionButton
                variant="ghost"
                size="sm"
                onClick={() =>
                  downloadImage(previewUrl, "face-check-snapshot.jpg")
                }
                className="rounded-full bg-white p-1.5 shadow-lg"
                title={t("faceLogs.downloadImage")}
              >
                <MdDownload className="h-5 w-5" />
              </ActionButton>
              <ActionButton
                variant="ghost"
                size="sm"
                onClick={() => setPreviewUrl(null)}
                className="rounded-full bg-white p-1.5 shadow-lg"
              >
                <MdClose className="h-5 w-5" />
              </ActionButton>
            </div>
            <img
              src={previewUrl}
              alt="Face check snapshot"
              className="max-h-[80vh] rounded-xl object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
}
